import { useId, useState } from 'react';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import styles from './FAQSection.module.css';

/**
 * FAQ — layout from Figma 249:5017 (a reference frame pasted into the file, so the shape is taken
 * and the copy is ours): heading on the left, accordion on the right, hairline under every row.
 *
 * One row open at a time, the first one open on arrival, matching the frame.
 */

export interface FAQItem {
  question: string;
  answer: string;
}

const DEFAULT_ITEMS: FAQItem[] = [
  {
    question: 'Is it free to start?',
    answer:
      'Yes. You get 100 credits a month and they refresh on their own. No card needed to try it.',
  },
  {
    question: 'What is a credit?',
    answer:
      'Credits are what Deck spends while it works. A long build with a strong model costs more, a small edit costs very little. If you run out before the month ends you can top up, and top up credits stay in your account instead of expiring.',
  },
  {
    question: 'Do I need to be a designer?',
    answer:
      'No. Tell Deck what you are explaining and who it is for, and it handles the look. If you want to art direct it yourself, you can.',
  },
  {
    question: 'Can I edit what it builds?',
    answer:
      'Yes, all of it. Every element on the canvas can be moved and restyled by hand, the way you would in Figma. Deck gives you a starting point, not something locked.',
  },
  {
    question: 'Where does it get my context?',
    answer:
      'From the tools you already use. Connect Figma, Linear or Notion and Deck pulls what it needs. You can also drop in your own documents, notes and links.',
  },
  {
    question: 'Which AI model does it use?',
    answer:
      'The strong ones are included and you choose per build. Heavier models do more and cost more credits.',
  },
  {
    question: 'What happens to my work?',
    answer:
      'It stays yours. We do not train on your work and we do not sell your data. What goes to the model is purged within 30 days.',
  },
  {
    question: 'Can I change or cancel my plan?',
    answer:
      'Any time, from your billing page. Moving up takes effect straight away and you only pay the difference for the rest of the month. Moving down or stopping keeps your credits until the period ends.',
  },
];

export interface FAQSectionProps {
  id?: string;
  className?: string;
  headline?: string;
  items?: FAQItem[];
}

export function FAQSection({
  id,
  className,
  headline = 'FAQs',
  items = DEFAULT_ITEMS,
}: FAQSectionProps) {
  const baseId = useId();
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section
      id={id}
      className={cn(landingType.sectionVerticalPadding, styles.section, className)}
      data-name="FAQSection"
    >
      <div className={cn(landingType.sectionInset, styles.container)}>
        <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
          {headline}
        </ScrollRevealHeadline>

        <div className={styles.list}>
          {items.map((item, i) => {
            const open = i === openIndex;
            const triggerId = `${baseId}-trigger-${i}`;
            const panelId = `${baseId}-panel-${i}`;

            return (
              <div key={item.question} className={styles.item}>
                {/* The button carries the heading so screen readers can jump question to question. */}
                <h3 className={styles.itemHeading}>
                  <button
                    type="button"
                    id={triggerId}
                    className={styles.trigger}
                    aria-expanded={open}
                    aria-controls={panelId}
                    // Clicking the open row closes it, so the whole list can be collapsed.
                    onClick={() => setOpenIndex(open ? -1 : i)}
                  >
                    <span className={styles.question}>{item.question}</span>
                    <span className={styles.icon} aria-hidden="true" />
                  </button>
                </h3>

                {/* Height comes from the 0fr -> 1fr grid row rather than a max-height guess, so the
                    panel opens to exactly its own height however long the answer is. */}
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={triggerId}
                  className={styles.panel}
                  data-open={open || undefined}
                >
                  <div className={styles.panelInner}>
                    <p className={styles.answer}>{item.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

FAQSection.displayName = 'FAQSection';
